import { useState } from 'react';
import { Copy, Check, ArrowSquareOut, CaretDown, Sparkle } from '@phosphor-icons/react';

const REQUIRED_SCOPES = [
  { name: 'Mail.Read', why: 'Lees inkomende e-mails per mailbox' },
  { name: 'Mail.Send', why: 'Stuur uitgaande e-mails namens de mailbox' },
  { name: 'Mail.ReadWrite', why: 'Markeer als gelezen, verplaats spam/bounce' },
  { name: 'User.Read.All', why: 'Lees mailbox-eigenaar metadata (naam/handtekening)' },
];

function CopyButton({ value, testid }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* ignore */ }
  };
  return (
    <button
      type="button"
      onClick={onCopy}
      data-testid={testid}
      className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] uppercase tracking-wider bg-zinc-100 hover:bg-zinc-200 transition-colors rounded-sm"
      title="Kopieer"
    >
      {copied ? <Check size={10} weight="bold" /> : <Copy size={10} />}
      {copied ? 'OK' : 'Kopieer'}
    </button>
  );
}

/**
 * Step-by-step Azure AD setup wizard. Embedded in the Graph credentials modal.
 * Auto-detects which steps are complete based on form values + status.
 */
export default function GraphSetupWizard({ slug, form, status, hasCredentials }) {
  const [open, setOpen] = useState(!hasCredentials);

  const redirectUri = `${window.location.origin}/api/profiles/${slug}/graph/callback`;
  const portalUrl = 'https://portal.azure.com/#view/Microsoft_AAD_RegisteredApps/ApplicationsListBlade';

  const steps = [
    { id: 1, title: 'App-registratie aanmaken in Azure', done: !!form.tenant_id && !!form.client_id },
    { id: 2, title: 'API-permissions instellen', done: hasCredentials },
    { id: 3, title: 'Client secret genereren & plakken', done: hasCredentials },
    { id: 4, title: 'Verbinding testen', done: status?.connected === true },
  ];
  const currentStep = steps.find(s => !s.done)?.id || steps.length;

  return (
    <div className="border border-blue-200 bg-blue-50/40 rounded-sm overflow-hidden mb-4" data-testid="graph-setup-wizard">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-blue-50 transition-colors"
        data-testid="wizard-toggle"
      >
        <div className="flex items-center gap-2">
          <Sparkle size={14} weight="fill" className="text-blue-600" />
          <span className="text-xs font-semibold text-blue-900">Azure AD Setup-wizard</span>
          <span className="text-[10px] uppercase tracking-wider bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded-sm">
            Stap {currentStep}/4
          </span>
        </div>
        <CaretDown size={11} className={`text-blue-600 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="px-4 py-3 border-t border-blue-200 space-y-4 text-xs">
          {/* Step 1 */}
          <div data-testid="wizard-step-1">
            <div className="flex items-center gap-2 mb-1.5">
              <StepBadge n={1} done={steps[0].done} active={currentStep === 1} />
              <span className="font-semibold">Maak een nieuwe App-registratie aan in Azure</span>
            </div>
            <ol className="ml-7 space-y-1 text-zinc-700 list-decimal">
              <li>
                Open{' '}
                <a href={portalUrl} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline inline-flex items-center gap-0.5" data-testid="wizard-portal-link">
                  Azure Portal → App registrations <ArrowSquareOut size={9} />
                </a>
              </li>
              <li>Klik <strong>"+ New registration"</strong> bovenin.</li>
              <li>
                Naam: bv. <code className="bg-white px-1 rounded">{slug}-mailer</code> · Account type: <em>Single tenant</em>
              </li>
              <li>
                Bij <strong>"Redirect URI (optioneel)"</strong> selecteer "Web" en plak:
                <div className="mt-1 flex items-center gap-1.5">
                  <code className="flex-1 bg-white border border-zinc-200 px-2 py-1 rounded-sm font-mono break-all">{redirectUri}</code>
                  <CopyButton value={redirectUri} testid="wizard-copy-redirect" />
                </div>
              </li>
              <li>Klik <strong>"Register"</strong> → kopieer Tenant ID en Client ID uit het overzichtsscherm naar de velden hieronder.</li>
            </ol>
          </div>

          {/* Step 2 */}
          <div data-testid="wizard-step-2">
            <div className="flex items-center gap-2 mb-1.5">
              <StepBadge n={2} done={steps[1].done} active={currentStep === 2} />
              <span className="font-semibold">API-permissions toevoegen</span>
            </div>
            <ol className="ml-7 space-y-1 text-zinc-700 list-decimal">
              <li>In de zijbalk klik <strong>"API permissions"</strong> → <strong>"Add a permission"</strong></li>
              <li>Kies <strong>Microsoft Graph</strong> → <strong>Application permissions</strong> (NIET Delegated)</li>
              <li>Vink onderstaande scopes aan:</li>
            </ol>
            <ul className="ml-12 mt-1.5 space-y-1">
              {REQUIRED_SCOPES.map(s => (
                <li key={s.name} className="flex items-center gap-2">
                  <code className="bg-white border border-zinc-200 px-1.5 py-0.5 rounded-sm font-mono text-[10px]">{s.name}</code>
                  <span className="text-[10px] text-zinc-500">— {s.why}</span>
                  <CopyButton value={s.name} testid={`wizard-copy-scope-${s.name}`} />
                </li>
              ))}
            </ul>
            <div className="ml-7 mt-2 px-2 py-1.5 bg-amber-50 border border-amber-200 rounded-sm text-[10px] text-amber-900">
              <strong>Vergeet niet</strong> bovenin op <strong>"Grant admin consent for {slug}"</strong> te klikken — anders kan de app geen e-mails lezen.
            </div>
          </div>

          {/* Step 3 */}
          <div data-testid="wizard-step-3">
            <div className="flex items-center gap-2 mb-1.5">
              <StepBadge n={3} done={steps[2].done} active={currentStep === 3} />
              <span className="font-semibold">Client secret aanmaken</span>
            </div>
            <ol className="ml-7 space-y-1 text-zinc-700 list-decimal">
              <li>Zijbalk → <strong>"Certificates &amp; secrets"</strong> → <strong>"+ New client secret"</strong></li>
              <li>Description: bv. <code className="bg-white px-1 rounded">{slug}-prod</code> · Expires: <strong>24 months</strong></li>
              <li>Klik <strong>"Add"</strong> en kopieer direct de <strong>"Value"</strong> kolom (NIET Secret ID — die is nutteloos).</li>
              <li>Plak in het veld "Client Secret" hieronder en klik <strong>"Opslaan"</strong>.</li>
            </ol>
            <div className="ml-7 mt-2 px-2 py-1.5 bg-red-50 border border-red-200 rounded-sm text-[10px] text-red-900">
              <strong>Let op:</strong> Microsoft toont de Value maar één keer. Sla &apos;m gelijk op — anders moet je opnieuw genereren.
            </div>
          </div>

          {/* Step 4 */}
          <div data-testid="wizard-step-4">
            <div className="flex items-center gap-2 mb-1.5">
              <StepBadge n={4} done={steps[3].done} active={currentStep === 4} />
              <span className="font-semibold">Verbinding testen</span>
            </div>
            <p className="ml-7 text-zinc-700">
              Na opslaan klik op <strong>&quot;Verbinding testen&quot;</strong> hieronder. Een succesbericht betekent dat het profiel direct e-mails kan synchroniseren — geen restart nodig.
            </p>
            {status?.connected === true && (
              <div className="ml-7 mt-1.5 text-[10px] text-emerald-700">
                ✓ Verbonden. Profiel <strong>{slug}</strong> is nu volledig operationeel.
              </div>
            )}
            {status?.error && (
              <div className="ml-7 mt-1.5 text-[10px] text-red-700">
                Fout: <code className="bg-white px-1 rounded">{status.error}</code> — controleer of admin consent is verleend (stap 2).
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function StepBadge({ n, done, active }) {
  return (
    <span
      className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold ${
        done
          ? 'bg-emerald-500 text-white'
          : active
          ? 'bg-blue-600 text-white ring-2 ring-blue-200'
          : 'bg-zinc-200 text-zinc-500'
      }`}
    >
      {done ? <Check size={11} weight="bold" /> : n}
    </span>
  );
}
